import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, ButtonBase } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LoadingSpinner from '../components/LoadingSpinner';

const PROBE_URL = '/';
const PROBE_TIMEOUT_MS = 3000;

const OfflineBanner = () => {
  const [status, setStatus] = useState(navigator.onLine ? 'online' : 'offline');
  const [isVisible, setIsVisible] = useState(!navigator.onLine);

  const handleDismiss = () => setIsVisible(false);

  const triggerSuccess = useCallback(() => {
    setStatus('success');
    setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => setStatus('offline'), 500);
    }, 2000);
  }, []);

  const probeNetwork = useCallback(async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

    try {
      const res = await fetch(PROBE_URL, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!res || !res.ok) throw new Error('offline');
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  const checkConnection = useCallback(async () => {
    setStatus('checking');
    const online = await probeNetwork();
    if (online) triggerSuccess();
    else setStatus('offline');
  }, [probeNetwork, triggerSuccess]);

  useEffect(() => {
    const handleOnline = () => triggerSuccess();
    const handleOffline = () => {
      setStatus('offline');
      setIsVisible(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [triggerSuccess]);

  if (!isVisible) return null;

  const isSuccess = status === 'success';
  const isChecking = status === 'checking';

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 48px)',
        maxWidth: '400px',
        bgcolor: isSuccess ? 'rgba(16, 185, 129, 0.9)' : 'rgba(17, 24, 39, 0.85)',
        backdropFilter: 'blur(24px) saturate(180%)',
        borderRadius: '24px',
        p: '6px 6px 6px 20px',
        zIndex: 9999,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: isSuccess
          ? '0 20px 40px rgba(16, 185, 129, 0.3)'
          : '0 20px 40px rgba(0, 0, 0, 0.6)',
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: 'slideUp 0.6s cubic-bezier(0.2, 1, 0.3, 1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          padding: '1.2px',
          background: isSuccess
            ? 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.15) 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          pointerEvents: 'none',
        },
        '@keyframes slideUp': {
          from: { transform: 'translateX(-50%) translateY(120%)', opacity: 0 },
          to: { transform: 'translateX(-50%) translateY(0)', opacity: 1 },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {isChecking ? (
          /* Scaled down for banner compatibility */
          <Box
            sx={{ transform: 'scale(0.6)', display: 'flex', width: 20, justifyContent: 'center' }}
          >
            <LoadingSpinner />
          </Box>
        ) : isSuccess ? (
          <CheckCircleRoundedIcon sx={{ fontSize: '1.2rem', color: 'white' }} />
        ) : (
          <Box
            sx={{
              width: 8,
              height: 8,
              bgcolor: '#ef4444',
              borderRadius: '50%',
              boxShadow: '0 0 12px #ef4444',
              animation: 'pulse 2s infinite',
            }}
          />
        )}

        <Box sx={{ fontSize: '0.8125rem', fontWeight: 600, letterSpacing: '0.01em' }}>
          {isChecking ? 'Reconnecting...' : isSuccess ? 'Back Online' : 'No internet connection'}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {!isChecking && !isSuccess && (
          <ButtonBase
            onClick={checkConnection}
            sx={{
              p: '6px 14px',
              borderRadius: '16px',
              fontSize: '0.7rem',
              fontWeight: 800,
              bgcolor: 'rgba(255,255,255,0.1)',
              transition: '0.2s',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            }}
          >
            RETRY
          </ButtonBase>
        )}

        <IconButton
          onClick={handleDismiss}
          size="small"
          sx={{
            color: 'rgba(255, 255, 255, 0.4)',
            '&:hover': { color: '#fff', bgcolor: 'transparent' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: '1.1rem' }} />
        </IconButton>
      </Box>

      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
          70% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
          100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
      `}</style>
    </Box>
  );
};

export default OfflineBanner;
